import { createApiInstance } from './axios-config';
import API_CONFIG from '../../config/apiConfig';

const api = createApiInstance(API_CONFIG.AGENT_SERVICE_URL);

export interface App {
  id: string;
  user_id: string;
  organization_id: string;
  conversation_id?: string;
  name: string;
  description?: string;
  html_content: string;
  assets: Record<string, string>;
  metadata: Record<string, any>;
  version: string;
  is_public: boolean;
  is_organization_shared: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateAppDto {
  name: string;
  description?: string;
  html_content: string;
  assets?: Record<string, string>;
  metadata?: Record<string, any>;
  conversation_id?: string;
  is_public?: boolean;
  is_organization_shared?: boolean;
}

export interface ShareAppDto {
  userId?: string;
  teamId?: string;
  permissionType: 'view' | 'edit' | 'admin';
}

export interface AppPermission {
  id: string;
  app_id: string;
  user_id?: string;
  team_id?: string;
  permission_type: 'view' | 'edit' | 'admin';
  created_at: string;
  user_email?: string;
  team_name?: string;
}

export class AppService {
  async getUserApps(includeShared = true) {
    const response = await api.get(`/api/v1/apps`, {
      params: { includeShared },
    });
    return response.data.apps;
  }

  async getPublicApps(limit = 20) {
    const response = await api.get(`/api/v1/apps/public`, {
      params: { limit },
    });
    return response.data.apps;
  }

  async getOrganizationApps() {
    const response = await api.get(`/api/v1/apps/organization`);
    return response.data.apps;
  }

  async createApp(data: CreateAppDto) {
    const response = await api.post(`/api/v1/apps`, data);
    return response.data;
  }

  async getApp(appId: string) {
    const response = await api.get(`/api/v1/apps/${appId}`);
    return response.data;
  }

  async updateApp(appId: string, data: Partial<CreateAppDto>) {
    const response = await api.patch(`/api/v1/apps/${appId}`, data);
    return response.data;
  }

  async deleteApp(appId: string) {
    await api.delete(`/api/v1/apps/${appId}`);
  }

  async shareApp(appId: string, data: ShareAppDto) {
    const response = await api.post(`/api/v1/apps/${appId}/share`, data);
    return response.data;
  }

  async getAppPermissions(appId: string) {
    const response = await api.get(`/api/v1/apps/${appId}/permissions`);
    return response.data.permissions;
  }

  async removePermission(appId: string, permissionId: string) {
    await api.delete(`/api/v1/apps/${appId}/permissions/${permissionId}`);
  }

  async exportApp(appId: string) {
    const response = await api.get(`/api/v1/apps/${appId}/export`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async getAppStats(appId: string) {
    const response = await api.get(`/api/v1/apps/${appId}/stats`);
    return response.data;
  }

  async createAppFromConversation(conversationId: string, messageId: string, name: string, description?: string) {
    const response = await api.post(`/api/v1/apps/from-conversation/${conversationId}`, 
      { messageId, name, description }
    );
    return response.data;
  }
}